import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  LocalHospital,
  ContentCopy,
  Delete,
  Edit as EditIcon,
  Save,
  Print,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ipdAPI, treatmentAPI } from '../../services/api';
import PrintHeader from '../shared/PrintHeader';

const TIME_SLOTS = ['Satkriya', 'Morning', 'Evening', 'Night'];

function TreatmentPlanManagement({ embedded = false, apiOverride, filterFrom, filterTo }) {
  const activeAPI = apiOverride || ipdAPI;
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};

  const [treatments, setTreatments] = useState([]);
  const [allPlans, setAllPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dateRange, setDateRange] = useState([]);

  // Edit state for specific cell
  const [editingCell, setEditingCell] = useState(null); // { date: 'YYYY-MM-DD', timeSlot: 'Morning' }
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Copy dialogs
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  const [copySlotDialogOpen, setCopySlotDialogOpen] = useState(false);
  const [copySourceDate, setCopySourceDate] = useState('');
  const [copySourceSlot, setCopySourceSlot] = useState('');
  const [copyTargetDates, setCopyTargetDates] = useState([]);

  useEffect(() => {
    generateDateRange();
    loadTreatments();
  }, [admissionId]);

  useEffect(() => {
    if (dateRange.length > 0) {
      loadAllTreatmentPlans();
    }
  }, [dateRange]);

  const generateDateRange = () => {
    if (!patientInfo.admissionDate || !patientInfo.plannedDays) {
      setError('Missing admission information. Please go back and try again.');
      return;
    }

    const admissionDate = new Date(patientInfo.admissionDate);
    const dates = [];

    for (let i = 0; i < patientInfo.plannedDays; i++) {
      const date = new Date(admissionDate);
      date.setDate(date.getDate() + i);
      dates.push(formatDate(date));
    }

    setDateRange(dates);
  };

  const loadTreatments = async () => {
    try {
      const response = await treatmentAPI.getAll();
      const data = response.data.data || response.data;
      setTreatments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Load treatments error:', err);
    }
  };

  const loadAllTreatmentPlans = async () => {
    setLoading(true);
    try {
      const plansData = {};

      for (const dateStr of dateRange) {
        const response = await activeAPI.getTreatmentPlanByDate(admissionId, dateStr);
        const data = response.data.data || response.data;

        if (Array.isArray(data)) {
          // API now returns grouped data with treatments array
          data.forEach(plan => {
            const key = `${dateStr}_${plan.timeSlot}`;
            plansData[key] = plan;
          });
        }
      }

      setAllPlans(plansData);
    } catch (err) {
      console.error('Load treatment plans error:', err);
      setError('Failed to load treatment plans');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    });
  };

  const handleEditCell = (dateStr, timeSlot) => {
    const key = `${dateStr}_${timeSlot}`;
    const plan = allPlans[key];

    setEditingCell({ date: dateStr, timeSlot });

    if (plan && plan.treatments) {
      // Map treatments from plan to selected format
      setSelectedTreatments(plan.treatments.map(t => ({
        id: t.treatmentId || `custom_${Date.now()}`,
        name: t.treatmentName,
        price: t.price || 0,
        isCustom: !t.treatmentId
      })));
      setInstructions(plan.instructions || '');
    } else {
      setSelectedTreatments([]);
      setInstructions('');
    }

    setEditDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setSelectedTreatments([]);
    setInstructions('');
    setEditDialogOpen(false);
  };

  const handleSaveTreatment = async () => {
    if (selectedTreatments.length === 0) {
      setError('Please select at least one treatment');
      return;
    }

    if (!editingCell) return;

    setSaving(true);
    setError('');

    try {
      // Prepare treatments array in the format expected by backend
      const treatments = selectedTreatments.map(treatment => ({
        treatmentId: treatment.isCustom ? null : treatment.id,
        treatmentName: treatment.name
      }));

      // Send all treatments in a single API call
      const treatmentData = {
        planDate: editingCell.date,
        timeSlot: editingCell.timeSlot,
        treatments: treatments,
        instructions: instructions.trim()
      };

      await activeAPI.createTreatmentPlan(admissionId, treatmentData);

      setSuccess('Treatment plan saved successfully');
      handleCancelEdit();
      loadAllTreatmentPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save treatment plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTreatment = async (dateStr, timeSlot) => {
    if (!window.confirm(`Are you sure you want to delete all treatments for this time slot?`)) {
      return;
    }

    try {
      const key = `${dateStr}_${timeSlot}`;
      const plan = allPlans[key];

      if (plan && plan.id) {
        await activeAPI.deleteTreatmentPlan(plan.id);
        setSuccess('Treatment plan deleted successfully');
        loadAllTreatmentPlans();
      } else {
        setError('Treatment plan not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete treatment plan');
    }
  };

  const handleCopyRow = (sourceDateStr) => {
    setCopySourceDate(sourceDateStr);
    setCopyTargetDates([]);
    setCopyDayDialogOpen(true);
  };

  const handleCopySlot = (dateStr, timeSlot) => {
    setCopySourceDate(dateStr);
    setCopySourceSlot(timeSlot);
    setCopyTargetDates([]);
    setCopySlotDialogOpen(true);
  };

  const handleConfirmCopyDay = async () => {
    if (copyTargetDates.length === 0) {
      setError('Please select at least one target date');
      return;
    }

    try {
      // Copy to multiple dates
      for (const targetDate of copyTargetDates) {
        await activeAPI.copyTreatmentDay({
          admissionId: parseInt(admissionId),
          sourceDate: copySourceDate,
          targetDate: targetDate
        });
      }
      setSuccess(`Entire day plan copied to ${copyTargetDates.length} date(s) successfully`);
      setCopyDayDialogOpen(false);
      loadAllTreatmentPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy plan');
    }
  };

  const handleConfirmCopySlot = async () => {
    if (copyTargetDates.length === 0) {
      setError('Please select at least one target date');
      return;
    }

    try {
      // Copy by fetching source and creating at target
      const sourceKey = `${copySourceDate}_${copySourceSlot}`;
      const sourcePlan = allPlans[sourceKey];

      if (!sourcePlan) {
        setError('Source treatment plan not found');
        return;
      }

      // Copy to multiple target dates
      for (const targetDate of copyTargetDates) {
        // Prepare treatments array
        const treatments = sourcePlan.treatments.map(treatment => ({
          treatmentId: treatment.treatmentId || null,
          treatmentName: treatment.treatmentName
        }));

        // Create treatment plans at each target date using new API format
        await activeAPI.createTreatmentPlan(admissionId, {
          planDate: targetDate,
          timeSlot: copySourceSlot,
          treatments: treatments,
          instructions: sourcePlan.instructions || ''
        });
      }

      setSuccess(`${copySourceSlot} treatment copied to ${copyTargetDates.length} date(s) successfully`);
      setCopySlotDialogOpen(false);
      loadAllTreatmentPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy treatment');
    }
  };

  const getSlotIcon = (timeSlot) => {
    switch (timeSlot) {
      case 'Satkriya': return '🙏';
      case 'Morning': return '🌅';
      case 'Evening': return '🌙';
      case 'Night': return '🌃';
      default: return '⏰';
    }
  };

  const getCellPlan = (dateStr, timeSlot) => {
    const key = `${dateStr}_${timeSlot}`;
    return allPlans[key];
  };

  return (
    <Box>
      {!embedded && (
        <PrintHeader
          title="Treatment Plan"
          patientInfo={patientInfo}
        />
      )}
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          {!embedded && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }} className="no-print">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/doctor', { state: { activeTab: 3 } })} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <LocalHospital sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  Treatment Plan Management - Tabular View
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Patient: {patientInfo.patientName} | Room: {patientInfo.roomNumber}
                </Typography>
                {patientInfo.plannedDays > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Admission: {new Date(patientInfo.admissionDate).toLocaleDateString('en-GB')} |
                    Duration: {patientInfo.plannedDays} days
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Print />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Snackbar>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : dateRange.length === 0 ? (
            <Alert severity="warning">
              No date range available. Please ensure admission date and planned days are set.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: 'secondary.main', color: 'white' }}>
                      Date
                    </TableCell>
                    {TIME_SLOTS.map((timeSlot) => (
                      <TableCell
                        key={timeSlot}
                        align="center"
                        sx={{ fontWeight: 'bold', minWidth: 250, bgcolor: 'secondary.main', color: 'white' }}
                      >
                        {getSlotIcon(timeSlot)} {timeSlot}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}
                      className="no-print"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((filterFrom || filterTo) ? dateRange.filter(d => (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo)) : dateRange).map((dateStr) => (
                    <TableRow key={dateStr} hover>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                        {formatDisplayDate(dateStr)}
                      </TableCell>
                      {TIME_SLOTS.map((timeSlot) => {
                        const plan = getCellPlan(dateStr, timeSlot);
                        return (
                          <TableCell
                            key={`${dateStr}_${timeSlot}`}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: plan ? 'secondary.50' : 'white',
                              '&:hover': { bgcolor: plan ? 'secondary.100' : 'grey.50' },
                              position: 'relative',
                              verticalAlign: 'top',
                              p: 1.5
                            }}
                          >
                            {plan ? (
                              <Box>
                                {plan.treatments && plan.treatments.length > 0 ? (
                                  <Box>
                                    {plan.treatments.map((treatment, idx) => (
                                      <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                                          • {treatment.treatmentName}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                                    {plan.treatmentName}
                                  </Typography>
                                )}
                                {plan.instructions && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>
                                    {plan.instructions}
                                  </Typography>
                                )}
                                <Box className="no-print" sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditCell(dateStr, timeSlot)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Copy to another day">
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => handleCopySlot(dateStr, timeSlot)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteTreatment(dateStr, timeSlot)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                className="no-print"
                                onClick={() => handleEditCell(dateStr, timeSlot)}
                                sx={{
                                  textAlign: 'center',
                                  color: 'text.secondary',
                                  py: 2,
                                  fontSize: '0.75rem'
                                }}
                              >
                                Click to add
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" className="no-print">
                        <Tooltip title="Copy entire day">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyRow(dateStr)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCell && `Edit Treatment - ${formatDisplayDate(editingCell.date)} at ${editingCell.timeSlot}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              multiple
              fullWidth
              options={treatments.map(t => ({ id: t.id, name: t.name, price: t.price, isCustom: false }))}
              value={selectedTreatments}
              onChange={(event, newValue) => {
                setSelectedTreatments(newValue);
              }}
              getOptionLabel={(option) => `${option.name}${option.price ? ` - ₹${option.price}` : ''}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Treatments (multiple allowed)"
                  placeholder="Select or type custom treatment"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option.name}
                    {...getTagProps({ index })}
                    color="secondary"
                    size="small"
                  />
                ))
              }
              freeSolo
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' && newInputValue && event?.type === 'change') {
                  // Allow custom treatment entry
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && event.target.value) {
                  const customTreatmentName = event.target.value;
                  if (customTreatmentName && !selectedTreatments.find(t => t.name === customTreatmentName)) {
                    setSelectedTreatments([
                      ...selectedTreatments,
                      { id: `custom_${Date.now()}`, name: customTreatmentName, price: 0, isCustom: true }
                    ]);
                  }
                  event.target.value = '';
                  event.preventDefault();
                }
              }}
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Tip: Select from the list or type a custom treatment name and press Enter
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter treatment instructions, focus areas, precautions, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTreatment}
            variant="contained"
            color="secondary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Day Dialog */}
      <Dialog open={copyDayDialogOpen} onClose={() => setCopyDayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy Entire Day Treatment Plan to Multiple Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Copy from: <strong>{formatDisplayDate(copySourceDate)}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
              <Button size="small" variant="outlined" color="secondary" onClick={() => setCopyTargetDates(dateRange.filter(d => d !== copySourceDate))}>
                Select All
              </Button>
              <Button size="small" variant="outlined" color="secondary" onClick={() => setCopyTargetDates([])}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Grid container spacing={0}>
                {dateRange.filter(date => date !== copySourceDate).map(date => (
                  <Grid item xs={6} sm={4} key={date}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyTargetDates.includes(date)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyTargetDates(prev => [...prev, date]);
                            } else {
                              setCopyTargetDates(prev => prev.filter(d => d !== date));
                            }
                          }}
                          size="small"
                          color="secondary"
                        />
                      }
                      label={<Typography variant="caption">{formatDisplayDate(date)}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This will overwrite any existing treatment plans for the selected dates.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDayDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCopyDay}
            variant="contained"
            color="secondary"
            disabled={copyTargetDates.length === 0}
          >
            Copy to {copyTargetDates.length} Date{copyTargetDates.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Slot Dialog */}
      <Dialog open={copySlotDialogOpen} onClose={() => setCopySlotDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy {copySourceSlot} Treatment to Multiple Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Copy from: <strong>{formatDisplayDate(copySourceDate)} - {copySourceSlot}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
              <Button size="small" variant="outlined" color="secondary" onClick={() => setCopyTargetDates(dateRange.filter(d => d !== copySourceDate))}>
                Select All
              </Button>
              <Button size="small" variant="outlined" color="secondary" onClick={() => setCopyTargetDates([])}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Grid container spacing={0}>
                {dateRange.filter(date => date !== copySourceDate).map(date => (
                  <Grid item xs={6} sm={4} key={date}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyTargetDates.includes(date)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyTargetDates(prev => [...prev, date]);
                            } else {
                              setCopyTargetDates(prev => prev.filter(d => d !== date));
                            }
                          }}
                          size="small"
                          color="secondary"
                        />
                      }
                      label={<Typography variant="caption">{formatDisplayDate(date)}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This will overwrite any existing {copySourceSlot} treatments for the selected dates.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopySlotDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCopySlot}
            variant="contained"
            color="secondary"
            disabled={copyTargetDates.length === 0}
          >
            Copy to {copyTargetDates.length} Date{copyTargetDates.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TreatmentPlanManagement;
